import { useEffect, useMemo, useState } from 'react';

import type { RichAircraft } from './data/enrich';
import { applySatelliteFilter } from './filters/satelliteFilterEngine';
import { classCounts } from './filters/filterEngine';
import { useLiveStates } from './data/useLiveStates';
import { useLiveVessels } from './data/useLiveVessels';
import { useSatelliteCatalog } from './data/useSatelliteCatalog';
import { useSelectedAircraftTrack } from './data/useSelectedAircraftTrack';
import { loadReference } from './data/reference';
import { useStore } from './store';
import { FlightMap } from './map/FlightMap';
import { FilterPanel } from './filters/FilterPanel';
import { AircraftPanel } from './details/AircraftPanel';
import { VesselPanel } from './details/VesselPanel';
import { SkyHud } from './ar/SkyHud';
import { SatelliteGlobeView } from './satellite/SatelliteGlobeView';
import { mapBridge } from './map/mapBridge';
import { CompassControl } from './map/CompassControl';
import { PitchControl } from './map/PitchControl';
import { PitchSlider } from './map/PitchSlider';
import { LayersControl } from './map/LayersControl';
import { cls } from './lib/format';

function GlobeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.8 4 6 4 9s-1.5 6.2-4 9M12 3c-2.5 2.8-4 6-4 9s1.5 6.2 4 9" />
    </svg>
  );
}

export default function App() {
  const appMode = useStore((s) => s.appMode);
  const setAppMode = useStore((s) => s.setAppMode);
  const arMode = useStore((s) => s.arMode);
  const filtersOpen = useStore((s) => s.filtersOpen);
  const setFiltersOpen = useStore((s) => s.setFiltersOpen);
  const filter = useStore((s) => s.filter);
  const vesselFilter = useStore((s) => s.vesselFilter);
  const satelliteFilter = useStore((s) => s.satelliteFilter);
  const selectedIcao = useStore((s) => s.selectedIcao);
  const selectedVesselId = useStore((s) => s.selectedVesselId);

  const live = useLiveStates();
  const selectedTrack = useSelectedAircraftTrack(selectedIcao);
  const [aircraftEnabled, setAircraftEnabled] = useState(() => mapBridge.getAircraftEnabled());
  const [vesselsEnabled, setVesselsEnabled] = useState(() => mapBridge.getVesselsEnabled());
  const liveVessels = useLiveVessels(vesselsEnabled);
  const satelliteCatalog = useSatelliteCatalog(appMode === 'satellite');

  useEffect(() => mapBridge.subscribeAircraft(setAircraftEnabled), []);
  useEffect(() => mapBridge.subscribeVessels(setVesselsEnabled), []);

  const selectedAc = useMemo(
    () =>
      selectedIcao
        ? (selectedTrack.aircraft ??
          live.aircraft.find((a) => a.icao === selectedIcao) ??
          null)
        : null,
    [selectedIcao, selectedTrack.aircraft, live.aircraft],
  );

  const selectedVessel = useMemo(
    () => (selectedVesselId ? (liveVessels.vessels.find((v) => v.id === selectedVesselId) ?? null) : null),
    [selectedVesselId, liveVessels.vessels],
  );

  const filteredSatellites = useMemo(
    () => applySatelliteFilter(satelliteCatalog.satellites, satelliteFilter),
    [satelliteCatalog.satellites, satelliteFilter],
  );

  useEffect(() => {
    loadReference().catch(() => {});
  }, []);

  const counts = useMemo(() => classCounts(live.aircraft), [live.aircraft]);
  const aircraftCount = counts.commercial + counts.military + counts.private + counts.government;

  const toggleGlobe = () => setAppMode(appMode === 'satellite' ? 'map' : 'satellite');

  const filterChipParts = [
    filter.classes.length && `${filter.classes.length} aircraft categories`,
    filter.iconKinds.length && `${filter.iconKinds.length} aircraft kinds`,
    filter.airlines.length && `${filter.airlines.length} airlines`,
    filter.types.length && `${filter.types.length} types`,
    filter.query && `“${filter.query}”`,
    vesselFilter.classes.length && `${vesselFilter.classes.length} vessel categories`,
    vesselFilter.iconKinds.length && `${vesselFilter.iconKinds.length} vessel kinds`,
    vesselFilter.flags.length && `${vesselFilter.flags.length} flags`,
    vesselFilter.query && `vessel “${vesselFilter.query}”`,
    satelliteFilter.kinds.length && `${satelliteFilter.kinds.length} sat kinds`,
    satelliteFilter.classes.length && `${satelliteFilter.classes.length} sat classes`,
    satelliteFilter.regimes.length && `${satelliteFilter.regimes.length} regimes`,
    satelliteFilter.owners.length && `${satelliteFilter.owners.length} owners`,
    satelliteFilter.query && `sat “${satelliteFilter.query}”`,
  ].filter(Boolean);

  return (
    <div className="relative h-full w-full overflow-hidden">
      {appMode === 'map' ? (
        <FlightMap live={live} liveVessels={liveVessels} selectedTrack={selectedTrack} />
      ) : (
        <SatelliteGlobeView catalog={satelliteCatalog} />
      )}

      <header
        className="pointer-events-none absolute inset-x-0 top-0 z-30 flex items-center justify-between gap-2 px-3"
        style={{ paddingTop: 'var(--safe-top)' }}
      >
        {appMode === 'map' ? (
          <div className="pointer-events-auto flex items-start gap-2">
            <div className="flex flex-col gap-1.5">
              <button
                onClick={() => setFiltersOpen(!filtersOpen)}
                className="rounded-lg bg-bg-panel/90 px-3 py-2 text-sm font-medium text-slate-100 shadow ring-1 ring-bg-border backdrop-blur"
                aria-label="Toggle filters"
              >
                <span className="mr-1">☰</span>Filters
              </button>
              <div className="inline-flex w-9 flex-col overflow-hidden rounded-lg bg-bg-panel/90 shadow ring-1 ring-bg-border backdrop-blur">
                <button
                  type="button"
                  onClick={() => mapBridge.zoomIn()}
                  className="flex aspect-square w-full items-center justify-center text-lg leading-none text-slate-100 hover:bg-white/5 active:bg-white/10"
                  aria-label="Zoom in"
                >
                  +
                </button>
                <div className="h-px bg-bg-border" />
                <button
                  type="button"
                  onClick={() => mapBridge.zoomOut()}
                  className="flex aspect-square w-full items-center justify-center text-lg leading-none text-slate-100 hover:bg-white/5 active:bg-white/10"
                  aria-label="Zoom out"
                >
                  −
                </button>
              </div>
              <CompassControl />
              <LayersControl vesselCount={vesselsEnabled ? liveVessels.vessels.length : 0} />
              <PitchSlider />
              <PitchControl />
            </div>
            <div className="rounded-lg bg-bg-panel/90 px-3 py-2 font-mono text-xs text-slate-300 shadow ring-1 ring-bg-border backdrop-blur">
              {live.loading && aircraftEnabled
                ? 'loading…'
                : [
                    aircraftEnabled ? `${aircraftCount} aircraft` : null,
                    vesselsEnabled ? `${liveVessels.vessels.length} vessels` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'map only'}
              {live.refreshing && aircraftEnabled && !live.loading ? (
                <span className="text-slate-500"> · updating</span>
              ) : null}
              {liveVessels.refreshing && vesselsEnabled && !liveVessels.loading ? (
                <span className="text-slate-500"> · vessels updating</span>
              ) : null}
              {aircraftEnabled && live.error ? <span className="text-bad"> · {live.error}</span> : null}
              {vesselsEnabled && liveVessels.error ? (
                <span className="text-bad"> · {liveVessels.error}</span>
              ) : null}
              {counts.military > 0 && aircraftEnabled ? (
                <span className="ml-1 text-mil">· {counts.military} mil</span>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="pointer-events-auto flex flex-col gap-1.5">
            <button
              onClick={() => setFiltersOpen(!filtersOpen)}
              className="rounded-lg bg-bg-panel/90 px-3 py-2 text-sm font-medium text-slate-100 shadow ring-1 ring-bg-border backdrop-blur"
              aria-label="Toggle satellite filters"
            >
              <span className="mr-1">☰</span>Filters
            </button>
          </div>
        )}

        <div className="pointer-events-auto flex flex-col gap-1.5">
          {(appMode === 'map' || appMode === 'satellite') && arMode === 'off' ? (
            <button
              onClick={() => window.__fsStartAr?.()}
              className="rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-bg shadow"
            >
              AR
            </button>
          ) : null}
          <button
            onClick={toggleGlobe}
            className={cls(
              'flex h-10 w-10 items-center justify-center rounded-lg shadow ring-1 ring-bg-border',
              appMode === 'satellite'
                ? 'bg-accent text-bg'
                : 'bg-bg-panel/90 text-slate-100 backdrop-blur hover:bg-white/5',
            )}
            aria-label={appMode === 'satellite' ? 'Back to map' : 'Satellite globe'}
            title={appMode === 'satellite' ? 'Back to map' : 'Satellite tracking'}
          >
            <GlobeIcon />
          </button>
        </div>
      </header>

      <FilterPanel
        aircraft={live.aircraft}
        vessels={liveVessels.vessels}
        satellites={filteredSatellites}
        allSatellites={satelliteCatalog.satellites}
      />

      {appMode === 'map' ? (
        <>
          <AircraftPanel aircraft={selectedIcao ? selectedAc : null} />
          <VesselPanel vessel={selectedVesselId ? selectedVessel : null} />
          <SkyHud aircraft={live.aircraft} />
        </>
      ) : null}

      {filterChipParts.length > 0 ? (
        <button
          onClick={() => setFiltersOpen(true)}
          className="absolute left-1/2 top-[calc(var(--safe-top)+58px)] z-20 -translate-x-1/2 rounded-full bg-bg-panel/90 px-3 py-1 text-xs text-slate-300 shadow ring-1 ring-bg-border"
        >
          {filterChipParts.join(' · ')}
        </button>
      ) : null}

      {vesselsEnabled && appMode === 'map' ? (
        <div
          className="pointer-events-none absolute bottom-2 right-2 z-20 rounded-md bg-bg-panel/80 px-2 py-1 text-[10px] text-slate-500 shadow ring-1 ring-bg-border backdrop-blur"
          style={{ paddingBottom: 'calc(var(--safe-bottom) + 0.25rem)' }}
        >
          Vessel data ©{' '}
          <a
            href="https://axiomoverwatch.io"
            target="_blank"
            rel="noopener noreferrer"
            className="pointer-events-auto text-accent hover:underline"
          >
            Axiom Overwatch
          </a>{' '}
          (CC-BY 4.0)
        </div>
      ) : null}
    </div>
  );
}
